import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SvgIconStyle from "~src/components/SvgIconStyle";
import BrandAvatar from "~src/components/BrandAvatar";
import BrandCover from "~src/components/BrandCover";

import { Brand } from "~src/types/brand";
import { followerCount, reviewsCount } from "~src/utils/brandHelpers";
import { BrandCardChart } from "./BrandScoreChart";
import { PATH_BRANDS } from "~src/routes/paths";

export const TrendingBrandsTile = ({ brand }: { brand: Brand }) => {
  const theme = useTheme();
  return (
    <Stack
      component={RouterLink}
      to={PATH_BRANDS.overview(brand.brand_id)}
      state={{ source: 'trending_brands', brandData: brand }}
      sx={{
        color: "text.primary",
        textDecoration: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        height: 432,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: "12px",
        backgroundColor: theme.palette.common.white,
        overflow: "hidden",
      }}
      data-testid="trending-brands-grid"
    >
      <BrandCover
        brandName={brand.brandname}
        coverImage={brand.coverimage}
        height="100px"
      />
      <Stack
        sx={{
          width: "100%",
          bottom: 48,
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            borderRadius: "100%",
            width: 102,
            height: 102,
            border: `4px solid ${theme.palette.common.white}`,
            alignItems: "center",
            mb: theme.spacing(2),
          }}
        >
          <BrandAvatar
            brandName={brand.brandname}
            profileImage={brand.profileimage}
            size={96}
            sx={{
              border: `0.75px solid ${theme.palette.grey[300]}`,
            }}
          />
        </Box>
        <BrandCardTitle brand={brand} />
        <Stack
          direction="row"
          spacing={2}
          sx={{
            my: 3,
          }}
        >
          <BrandCardChart
            label="Scale"
            number={brand.scaling_score_ranking || 0}
          />
          <BrandCardChart
            label="Revenue"
            number={brand.revenue_score_ranking || 0}
          />
        </Stack>
        <BrandSocialIcons brand={brand} />
      </Stack>
    </Stack>
  );
};

const BrandCardTitle = ({ brand }: { brand: Brand }) => {
  return (
    <Stack
      spacing={1}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "75%",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          overflow: "hidden",
          whiteSpace: "normal",
          WebkitLineClamp: 2,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
        }}
      >
        {brand.brandname}
      </Typography>
      <Stack direction="row" spacing={2}>
        <Typography
          variant="caption"
          sx={{
            color: "grey.400",
            textAlign: "center"
          }}>
          {followerCount(brand)} followers
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "grey.400",
            textAlign: "center"
          }}>
          {reviewsCount(brand)} reviews
        </Typography>
      </Stack>
      <Chip label={brand.category_name} />
    </Stack>
  );
};

const BrandSocialIcons = ({ brand }: { brand: Brand }) => {
  return (
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
      {brand.brandsite && (
        <SvgIconStyle
          src="/assets/icons/social/website.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand.fburl && (
        <SvgIconStyle
          src="/assets/icons/social/facebook.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand.shopifysite && (
        <SvgIconStyle
          src="/assets/icons/social/shopify.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand.instagramurl && (
        <SvgIconStyle
          src="/assets/icons/social/instagram.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand.trustpilotsite && (
        <SvgIconStyle
          src="/assets/icons/social/trustpilot.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
    </Stack>
  );
};
